import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PaymentSuccessPage() {
  const { token, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (!sessionId || !token) { navigate('/'); return; }

    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          await refreshUser();
        } else if (attempts < 5) {
          setTimeout(() => setAttempts(a => a + 1), 2000);
        } else {
          setStatus('timeout');
        }
      } catch (err) {
        setStatus('error');
      }
    };

    checkStatus();
  }, [attempts, location, token, navigate, refreshUser]);

  return (
    <div className="min-h-screen hero-gradient pt-24 pb-12 flex items-center justify-center">
      <div className="glass rounded-2xl p-8 max-w-md mx-auto text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-12 h-12 animate-spin text-emerald-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white">{t('payment_checking')}</h2>
          </>
        )}
        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-emerald-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">{t('payment_success')}</h2>
            <p className="text-slate-400 mb-6">{t('payment_credits_added')}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 rounded-full bg-gradient-to-r from-emerald-500 to-blue-600 text-white font-medium"
            >
              {t('payment_start')}
            </button>
          </>
        )}
        {(status === 'error' || status === 'timeout') && (
          <>
            <AlertTriangle className="w-16 h-16 text-rose-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">{t('payment_error')}</h2>
            <p className="text-slate-400">{t('payment_error_msg')}</p>
          </>
        )}
      </div>
    </div>
  );
}
